import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HeartHandshake } from 'lucide-react';
import { charitySummary } from '@/lib/api';
import { formatGBPfromPence, formatDateLong } from '@/lib/format';

// Giving Back — DCMS Prize Draws & Competitions Code of Practice, clause 2.6.
// Commitment: 10% of net profit, paid to The Padel Foundation at the end of
// each financial year. Our year-end is 31 March (aligned to the UK tax year).
// The first contribution log entry will appear here after year-end 31/03/2026.

const GivingBackPage = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    charitySummary()
      .then(setData)
      .catch(() => setData({ total_pence: 0, contributions: [], charities: [], count: 0 }));
  }, []);

  const total = data?.total_pence || 0;
  const contributions = data?.contributions || [];
  const charities = data?.charities || [];

  return (
    <div className="container-pd py-10 md:py-16 max-w-5xl" data-testid="giving-back-page">
      <div className="font-mono text-[10px] text-[color:var(--pd-gold)] uppercase tracking-[0.2em]">Giving back</div>
      <h1 className="font-display text-4xl md:text-6xl leading-[1.05] md:leading-none mt-3">10% of profit. Every year. For the sport.</h1>
      <p className="mt-4 md:mt-5 text-base md:text-lg text-muted-sage leading-relaxed max-w-3xl">
        A fixed share of what we earn goes back into padel &mdash; not as a marketing line, but as an actual, dated, published transfer.
      </p>

      {/* Commitment block */}
      <div className="mt-6 md:mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        <div className="rounded-2xl border border-[color:var(--pd-gold)]/50 bg-surface p-5 md:p-8" data-testid="giving-commitment">
          <div className="font-mono text-[10px] text-[color:var(--pd-gold)] uppercase tracking-widest">What we donate</div>
          <h2 className="font-display text-2xl md:text-3xl mt-2 leading-tight">10% of net profit to The Padel Foundation.</h2>
          <p className="mt-3 md:mt-4 text-sm text-muted-sage leading-relaxed">
            A UK organisation supporting the growth of padel at community and grassroots level.
          </p>
          <p className="mt-2 md:mt-3 text-sm text-muted-sage leading-relaxed">
            <span className="text-[color:var(--pd-text)]">Paid each year</span> after our financial year-end on 31 March. First payment relates to year ending <span className="text-[color:var(--pd-text)]">31 March 2027</span>.
          </p>
        </div>

        <div className="rounded-2xl border border-pd bg-surface p-5 md:p-8" data-testid="giving-running-total">
          <div className="flex items-start gap-4">
            <div className="rounded-xl bg-[color:var(--pd-bg)] p-3 border border-pd">
              <HeartHandshake size={22} className="text-[color:var(--pd-gold)]" />
            </div>
            <div>
              <div className="font-mono text-[10px] text-muted-sage uppercase">Donated so far</div>
              <div className="font-display text-4xl md:text-5xl mt-1 text-[color:var(--pd-accent-yellow)]">{formatGBPfromPence(total)}</div>
              {contributions.length === 0 ? (
                <p className="mt-2 md:mt-3 text-sm text-muted-sage">
                  First contribution publishes after 31 March 2027.
                </p>
              ) : (
                <p className="mt-2 md:mt-3 text-sm text-muted-sage">
                  Across {contributions.length} contribution{contributions.length === 1 ? '' : 's'}
                  {charities.length > 0 ? <span> to {charities.length} organisation{charities.length === 1 ? '' : 's'}</span> : null}.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Log */}
      <div className="mt-8 md:mt-10">
        <div className="font-mono text-[10px] text-[color:var(--pd-gold)] uppercase tracking-widest">Contribution log</div>
        <h2 className="font-display text-2xl md:text-3xl mt-2">Every donation, dated and named.</h2>

        {contributions.length === 0 ? (
          <div className="mt-4 md:mt-6 rounded-2xl border border-pd bg-surface p-5 md:p-6" data-testid="giving-empty">
            <p className="text-sm text-muted-sage">
              The first row will appear here after 31 March 2027. Each entry shows the receiving organisation, the amount and the transfer date. Rows are only ever added &mdash; never edited or removed.
            </p>
          </div>
        ) : (
          <div className="mt-6 rounded-2xl border border-pd bg-surface overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[color:var(--pd-bg)]">
                  <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-sage">
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Charity</th>
                    <th className="px-4 py-3">Competition</th>
                    <th className="px-4 py-3 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {contributions.map((c, i) => (
                    <tr key={c.id} className={i % 2 === 0 ? 'bg-transparent' : 'bg-[color:var(--pd-bg)]/40'} data-testid="giving-row">
                      <td className="px-4 py-3 text-muted-sage">{formatDateLong(c.contributed_at)}</td>
                      <td className="px-4 py-3 text-[color:var(--pd-text)]">{c.charity_name}</td>
                      <td className="px-4 py-3">
                        {c.competition_slug ? (
                          <Link to={`/competitions/${c.competition_slug}`} className="text-muted-sage hover:text-[color:var(--pd-accent-yellow)]">{c.competition_title}</Link>
                        ) : (
                          <span className="text-muted-sage">&mdash;</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-[color:var(--pd-accent-yellow)]">{formatGBPfromPence(c.amount_pence)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <p className="mt-8 md:mt-10 text-xs text-muted-sage">
        Published under DCMS Prize Draws &amp; Competitions Code clause 2.6. If The Padel Foundation is unable to receive a contribution in any given year, we will publish the alternative recipient here with the same detail.
      </p>
    </div>
  );
};

export default GivingBackPage;
